import { Order, OrderConfigResponse } from '@ca/common'
import { captureException } from '@sentry/react-native'
import { RouterInputs } from 'api'
import { debugOrder } from './getDebugLog'
import { OrderCompare } from './orderCompare'

type OrderPatch = RouterInputs['order']['patch']

// No-write submits whose data failed to round-trip before we consider ourselves stuck
const MAX_LOSSY_SUBMITS = 5

// What we ask the server to do, without the response fields we hand back to it
const getSubmitKey = (data: OrderPatch) =>
  JSON.stringify({
    invoice_id: data.invoice_id,
    orders: data.orders.map((order) => ({
      updated: order.updated,
      data: order.data,
    })),
  })

export type OrderSubmitGuard = ReturnType<typeof createOrderSubmitGuard>

// Detects a patch loop: the same submit repeating, or one whose data never
// sticks. Owns its own mutable state so it can be created once per hook
// instance and tested without React.
export const createOrderSubmitGuard = () => {
  let lastSubmit: string | null = null
  let lossySubmits = 0
  let reportedStuck = false

  const reportStuckSubmit = (
    reason: string,
    incoming: Order[],
    current: Order[]
  ) => {
    const paths = incoming.flatMap((order) => {
      const stored = current.find((c) => c.localId === order.localId)
      return OrderCompare.getChangedPaths(order, stored).map(
        (path) => `${order.localId}.${path}`
      )
    })
    debugOrder.log(reason, { paths })
    if (reportedStuck) {
      return
    }
    reportedStuck = true
    captureException(new Error(reason), {
      level: 'warning',
      tags: {
        location: 'order/configure',
      },
      extra: {
        paths,
        orderIds: current.map((order) => order.localId),
      },
    })
  }

  return {
    // The response echoes what we send, so an identical submit changes
    // nothing. Returns true when the caller should not submit.
    shouldSuppress: (
      data: OrderPatch,
      incoming: Order[],
      current: Order[],
      isValid: boolean
    ): boolean => {
      if (isValid && lastSubmit && lastSubmit === getSubmitKey(data)) {
        reportStuckSubmit('Repeated order submit suppressed', incoming, current)
        return true
      }
      // No-write submits whose data never comes back intact mean we send a
      // field the server does not store, worth knowing about but safe to let through
      if (lossySubmits >= MAX_LOSSY_SUBMITS) {
        reportStuckSubmit(
          'Order data is not surviving the round trip',
          incoming,
          current
        )
      }
      return false
    },

    recordSuccess: (req: OrderPatch, res: OrderConfigResponse) => {
      // Only what the server accepted, a submit the debounce dropped never happened
      lastSubmit = getSubmitKey(req)
      const isWrite = req.orders.some((order) => order.updated)
      // Data the server stores comes back intact, so a lossy echo on a
      // no-write patch is the only thing that can sustain a patch loop
      const isLossy = req.orders.some((order) => {
        const echoed = res.orders.find(
          (o) => o.data.localId === order.data.localId
        )
        return (
          !echoed || !OrderCompare.isSameOrder(order.data as Order, echoed.data)
        )
      })
      lossySubmits = !isWrite && isLossy ? lossySubmits + 1 : 0
      if (isWrite) {
        reportedStuck = false
      }
    },

    // A manual submit is always allowed to go out again
    reset: () => {
      lastSubmit = null
      lossySubmits = 0
    },
  }
}
